import { I18n, Translation } from "../hooks/useTranslations";

const MILE_IN_KM = 1.609344;

export const formatBoolean = (t: Translation, value?: boolean): string => (value ? t.common.yes : t.common.no);

export const formatDate = (value: string, locale: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  return value ? new Date(value).toLocaleDateString(locale, options) : "";
};

export const formatNumber = (
  value: number,
  locale: string,
  { fractionalDigits = 2, showThousendSeparator = false },
): string =>
  value.toLocaleString(locale, {
    minimumFractionDigits: fractionalDigits,
    maximumFractionDigits: fractionalDigits,
    useGrouping: showThousendSeparator,
  });

export const formatTimeStamp = (value: string, locale: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };
  return new Date(value).toLocaleDateString(locale, options);
};

// improved function in order to add a space between the currency symbol and the value
export const formatCurrency = (value: number, locale: string, currency?: string): string => {
  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency || "EUR",
    currencyDisplay: "narrowSymbol",
    useGrouping: true,
  });

  const partsArray = formatter.formatToParts(value);
  const currencyReturn = partsArray.find((p) => p.type === "currency")?.value ?? "";
  const number = partsArray
    .filter((p) => p.type !== "currency")
    .map((p) => p.value)
    .join("");

  return `${currencyReturn}${number}`;
};

export const convertLocalDateToUTC = (localValue: string): string => {
  return new Date(localValue).toISOString();
};

export const getDefaultDateForm = (startDate?: string, endDate?: string): string => {
  if (!startDate || endDate) {
    return "";
  }

  const t = new Date();
  t.setHours(0, 0, 0, 0);
  const st = new Date(startDate);
  st.setHours(0, 0, 0, 0);

  if (t.toISOString() > st.toISOString()) {
    const yyyy = t.getFullYear();
    const mm = String(t.getMonth() + 1).padStart(2, "0");
    const dd = String(t.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  return "";
};

export const formatDateNumeric = (value: string, locale: string): string => {
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(value));
};

export const formatTimeStampNumeric = (value: string, locale: string): string => {
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
};

export const convertKilometersToMiles = (value: number): number => {
  return Math.round(value / MILE_IN_KM);
};

export const convertMilesToKilometers = (value: number): number => {
  return Math.round(value * MILE_IN_KM);
};

export const getErrorRenewDueDate = (enteredDate: string, recordDate: string, i18n: I18n): string => {
  return `${i18n.t.common.enteredDate}: ${formatDateNumeric(enteredDate, i18n.language)} ${
    i18n.t.common.lessThanDueDateRecord
  }: ${formatDateNumeric(recordDate, i18n.language)}`;
};

// export const getErrorMileage = (i18n: I18n, startDate: string, endDate?: string): string =>
//   endDate
//     ? `Date must be between ${formatDateNumeric(startDate, i18n.language)} and ${formatDateNumeric(
//         endDate,
//         i18n.language,
//       )}`
//     : `Date can't be before ${formatDateNumeric(startDate, i18n.language)}`;

export const getTitle = (
  startDate: string,
  endDate: string,
  first: string,
  second: string,
  considerTime?: boolean,
): string => {
  const end = new Date(endDate);
  const start = new Date(startDate);
  const today = new Date();
  !considerTime && today.setHours(2, 0, 0, 0);
  if (start <= today && (!endDate || end >= today)) {
    return first;
  }
  return second;
};

export const getWarningBoxTitle = (t: Translation, type: string): string => {
  if (type == "car-pool") {
    return t.common.currentlyInCarPool;
  }
  return t.common.currentlyNotUsed;
};

export const isRothoblaasEmail = (email: string | null | undefined): boolean =>
  !!email?.toLowerCase().endsWith("@rothoblaas.com");

export const isValidEmail = (email: string | null | undefined): boolean => {
  if (!email) return false;

  const basicEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return basicEmailRegex.test(email);
};

export const downloadFileFromData = (data: string, fileName: string, contentType?: string) => {
  const toString = function (a: Object) {
    return String(a);
  };

  const myBlob = (window.Blob || toString).bind(window);

  const anchor = document.createElement("a");

  const d = window.atob(data);

  const tType = contentType || "application/octet-stream";

  if (/([\x80-\xff])/.test(d)) {
    const tempUiArr = new Uint8Array(d.length);
    const mx = tempUiArr.length;

    for (let i = 0; i < mx; i++) {
      tempUiArr[i] = d.charCodeAt(i);
    }

    const o = new myBlob([tempUiArr], { type: tType });
    anchor.href = window.URL.createObjectURL(o);
  } else {
    const o = new myBlob([d || ""], { type: tType });
    anchor.href = window.URL.createObjectURL(o);
  }

  anchor.setAttribute("download", fileName);
  anchor.className = "download-js-link";
  anchor.innerHTML = "downloading...";
  anchor.style.display = "none";
  document.body.appendChild(anchor);

  setTimeout(function () {
    anchor.click();
    document.body.removeChild(anchor);
    setTimeout(function () {
      window.URL.revokeObjectURL(anchor.href);
    }, 250);
  }, 66);

  return true;
};

export const generateSecret = (length: number) => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmopqrstuvwxyz0123456789!()_=";
  let result = "";
  let counter = 0;
  while (counter < length) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
    counter++;
  }
  return result;
};

export const isOlderThanAWeek = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs: number = now.getTime() - date.getTime();
  const twoWeekMs = 2 * 7 * 24 * 60 * 60 * 1000; // two weeks
  return diffMs >= twoWeekMs;
};

export const isWithinLast48h = (createdAt?: string | Date | null): boolean => {
  if (!createdAt) return false;

  const createdAtDate = createdAt instanceof Date ? createdAt : new Date(createdAt);

  const twoDaysAgo = new Date();
  twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
  return createdAtDate >= twoDaysAgo;
};

export function sameDay(a?: Date | null, b?: Date | null): boolean {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export const getRowTitle = (allPermissions: string): string => {
  const rtn = allPermissions.split(":")[0] ?? "";
  return rtn;
};

export const isOutPrevious7days = (createdAt: string) => {
  const sevenDaysAgo = new Date();
  const createdAtDate = new Date(createdAt);

  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const rtn = createdAtDate <= sevenDaysAgo;
  return rtn;
};

export const isImage = (fileName: string) => {
  return ["png", "jpg", "gif", "jpeg", "tif", "svg"].some((x) => fileName.toLocaleLowerCase().split(".").pop() === x);
};
export const isVideo = (fileName: string) => {
  return ["mp3", "mp4", "avi"].some((x) => fileName.toLocaleLowerCase().split(".").pop() === x);
};
export const isEmbed = (fileName: string) => {
  return ["pdf"].some((x) => fileName.toLocaleLowerCase().split(".").pop() === x);
};

export const userAgent = window.navigator.userAgent;
export const isMobile = /Mobi|Android/i.test(userAgent);

export const getMimeType = (fileName: string): string => {
  const ext = fileName.split(".").pop()?.toLowerCase();

  switch (ext) {
    case "png":
      return "data:image/png;base64,";
    case "jpg":
    case "jpeg":
      return "data:image/jpeg;base64,";
    case "webp":
      return "data:image/webp;base64,";
    case "gif":
      return "data:image/gif;base64,";
    case "svg":
      return "data:image/svg+xml;base64,";

    case "mp4":
      return "data:video/mp4;base64,";
    case "webm":
      return "data:video/webm;base64,";
    case "ogg":
      return "data:video/ogg;base64,";
    case "mov":
      return "data:video/quicktime;base64,";

    case "pdf":
      return "data:application/pdf;base64,";

    default:
      return "data:application/octet-stream;base64,";
  }
};
